import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { Box, Button, ButtonGroup, Heading, ProgressBar, Text } from '@zillow/constellation-10';
import { IconArrowLeftOutline, IconArrowRightOutline } from '@zillow/constellation-icons';

import {
  useRentalManagerUserPropertyFeedToManualWebApiHandler_convertAll,
  useRentalManagerUserPropertyFeedToManualWebApiHandler_statuses,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { getConversionRate } from '../../../utils/listingClaimUtils';

import QueryConstants from '../../../../srcLegacy/shared/constants/QueryConstants';
import { ROUTES } from '../../../constants/urls';
import { STEP } from '../constants';

import { useAuth } from '../../../components/AuthProvider';
import InfoList from './components/InfoList';
import NothingToTransfer from './components/NothingToTransfer';
import Transferred from './components/Transferred';

import HomeBrowser from '../../../assets/HomeBrowser.svg';
import Notification from '../../../assets/Notification.svg';
import Trophy from '../../../assets/Trophy.svg';

import WizardBodyTemplate from '../../WizardBodyTemplate';

import type { WizardBodyTemplateProps } from '../../WizardStepController';

const STEPS = {
  INIT: 'init',
  TRANSFERRING: 'transferring',
  TRANSFERRED: 'transferred',
  NOTHING_TO_TRANSFER: 'nothingToTransfer',
} as const;

const delayedTransferMessage = (
  <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Text textStyle="body-sm" css={{ color: 'text.feedback.critical.hero', animation: 'fadeIn ease 2s' }}>
      While we prepare your listings for viewing, you can go to your properties.
    </Text>
  </Box>
);

const infos = [
  'Manage how your listing appears on Zillow.',
  'Create virtual tours via the Zillow 3D Home Tours app.',
  'Set up online applications and lease signing.',
];

const WizConvertListingInfo = ({ onGoToPrevStep, onGoToStep, progressStepper }: WizardBodyTemplateProps) => {
  const router = useRouter();
  const { refetch: refetchUser, user } = useAuth();

  const [hasError, setHasError] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [currentStep, setCurrentStep] = useState<(typeof STEPS)[keyof typeof STEPS]>(STEPS.INIT);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isTransferDelayed, setIsTransferDelayed] = useState(false);

  const { mutateAsync: convertAll } = useRentalManagerUserPropertyFeedToManualWebApiHandler_convertAll();
  const { data: statusesData, refetch: refetchStatuses } =
    useRentalManagerUserPropertyFeedToManualWebApiHandler_statuses({
      enabled: currentStep === STEPS.TRANSFERRING,
      refetchInterval: (query) => {
        const state = query.state.data as { count?: { failed: number; succeeded: number; total: number } } | undefined;
        const count = state?.count;
        if (count && count.total > 0 && count.failed + count.succeeded < count.total) {
          return 1000;
        }
        return false;
      },
    });

  const totalListingCount = user?.summary?.totalListingCount ?? null;
  const count = statusesData?.count;
  const conversionPercentage =
    count && count.total > 0
      ? getConversionRate({ failed: count.failed, succeeded: count.succeeded, total: count.total })
      : progressPercentage;

  useEffect(() => {
    if (currentStep === STEPS.TRANSFERRING && count) {
      const pct = getConversionRate({
        failed: count.failed,
        succeeded: count.succeeded,
        total: count.total,
      });
      setProgressPercentage(pct);
      if (pct >= 100 || count.failed + count.succeeded >= count.total) {
        setIsTransferring(false);
        setCurrentStep(STEPS.TRANSFERRED);
      }
    }
  }, [currentStep, count]);

  useEffect(() => {
    if (currentStep === STEPS.TRANSFERRING) {
      const t = setTimeout(() => setIsTransferDelayed(true), 10000);
      return () => clearTimeout(t);
    }
    if (currentStep === STEPS.TRANSFERRED) {
      setIsTransferDelayed(false);
    }
  }, [currentStep]);

  const handleFindListings = () => {
    onGoToStep(STEP.FIND_LISTING_RESULTS);
  };

  const handleViewProperties = () => {
    router.push(ROUTES.PROPERTIES);
  };

  const handleGoToNextStep = async () => {
    if (currentStep === STEPS.NOTHING_TO_TRANSFER) {
      const openAddPropertyModal = totalListingCount != null && totalListingCount === 0;
      router.push({
        pathname: ROUTES.PROPERTIES,
        ...(openAddPropertyModal ? { query: { [QueryConstants.POSTING_PATH]: 'true' } } : {}),
      });
    }
    if (currentStep === STEPS.INIT) {
      setIsTransferring(true);
      try {
        await convertAll();
        setCurrentStep(STEPS.TRANSFERRING);
        refetchStatuses();
      } catch {
        try {
          await refetchUser();
          setCurrentStep(STEPS.NOTHING_TO_TRANSFER);
          setIsTransferring(false);
        } catch {
          setHasError(true);
        }
      }
    }
    if (currentStep === STEPS.TRANSFERRING) {
      setCurrentStep(STEPS.TRANSFERRED);
    }
  };

  const STEP_CONTENT_CONFIGS = {
    [STEPS.INIT]: {
      title: 'Last step, transfer!',
      subtitle: (
        <span>
          You’re ready to transfer your listings to Zillow Rental Manager.
          <br />
          After transferring, you can:
        </span>
      ),
      nextLabel: 'Next',
      icon: <Image src={HomeBrowser} alt="HomeBrowser" fill />,
      content: <InfoList infos={infos} />,
    },
    [STEPS.TRANSFERRING]: {
      title: 'Transferring...',
      subtitle: <Text css={{ animation: 'fadeIn ease 2s' }}>This will only take a second...</Text>,
      nextLabel: 'Next',
      icon: <Image src={HomeBrowser} alt="HomeBrowser" fill />,
      content: (
        <>
          <ProgressBar value={conversionPercentage} css={{ marginTop: '400', marginBottom: '200' }} />
          <InfoList infos={infos} />
          {isTransferDelayed && (
            <Button
              tone="brand"
              emphasis="filled"
              onClick={handleViewProperties}
              icon={<IconArrowRightOutline />}
              iconPosition="end"
              css={{ animation: 'fadeIn ease 2s', alignSelf: 'center' }}
            >
              View properties
            </Button>
          )}
        </>
      ),
    },
    [STEPS.TRANSFERRED]: {
      title: (
        <Heading level={1} textStyle="heading-md">
          Transfer completed
        </Heading>
      ),
      subtitle: undefined,
      nextLabel: undefined,
      icon: <Image src={Trophy} alt="Trophy" fill />,
      content: (
        <Transferred
          onSearchByOtherContactMethodsClick={handleFindListings}
          onViewPropertiesClick={handleViewProperties}
        />
      ),
    },
    [STEPS.NOTHING_TO_TRANSFER]: {
      title: 'There are no listings to transfer',
      nextLabel: 'View properties',
      icon: <Image src={Notification} alt="Notification" fill />,
      content: <NothingToTransfer onFindListingsClick={handleFindListings} />,
    },
  };

  const config = STEP_CONTENT_CONFIGS[currentStep];
  const subtitle = isTransferDelayed ? delayedTransferMessage : (config as { subtitle?: React.ReactNode }).subtitle;

  return (
    <WizardBodyTemplate
      title={config.title}
      subtitle={subtitle}
      hasError={hasError}
      icon={config.icon}
      progressStepper={progressStepper}
      footer={
        <ButtonGroup aria-label="Actions" css={{ justifyContent: 'center' }}>
          {currentStep !== STEPS.TRANSFERRED && (
            <Button
              tone="brand"
              emphasis="outlined"
              onClick={onGoToPrevStep ?? undefined}
              icon={<IconArrowLeftOutline />}
            >
              Back
            </Button>
          )}
          {!isTransferDelayed && currentStep !== STEPS.TRANSFERRED && (
            <Button
              tone="brand"
              emphasis="filled"
              onClick={handleGoToNextStep}
              disabled={isTransferring}
              icon={<IconArrowRightOutline />}
              iconPosition="end"
            >
              {(config as { nextLabel?: string }).nextLabel ?? 'Next'}
            </Button>
          )}
        </ButtonGroup>
      }
    >
      {config.content}
    </WizardBodyTemplate>
  );
};

export default WizConvertListingInfo;
